import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import Modal from '../../BaseComponents/Modal';
import {
  createNutritionist,
  getNutritionistsList,
  updateNutritionist
} from '../../../api/nutritionist';
import './NutritionistModal.scss';
import TextInput from '../../BaseComponents/TextInput';
import TextArea from '../../BaseComponents/TextArea';
import Button from '../../BaseComponents/Button';
import { useDispatch, useSelector } from 'react-redux';
import {
  NutritionistsPageState,
  setNutritionistsCount,
  setNutritionistsList,
  setNutritionistDataToEdit
} from '../../../store/nutritionistsSlice';
import UploadPhoto from '../../UploadPhoto';
import { deleteFile } from '../../../api/fileStorage';
import PhoneInputComp from '../../BaseComponents/PhoneInput';
import { toast } from 'react-toastify';
import Select, { SelectOption } from '../../BaseComponents/Select';
import { getTeamsList } from '../../../api/teams';

type TypeName = {
  isOpen: boolean;
  onClose: () => void;
};

interface FormValues {
  first_name: string;
  last_name: string;
  bio: string;
  email: string;
  number: string;
  photo: string;
  calendly_url: string;
  photo_url?: any;
}

const NutritionistModal: React.FC<TypeName> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const {
    nutritionistDataToEdit,
    nutritionistsList,
    nutritionistsCount,
    limit
  } = useSelector(
    (state: { nutritionists: NutritionistsPageState }) => state.nutritionists
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFormError, setIsFormError] = useState<any>(false);
  const [wasModalChanged, setWasModalChanged] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<SelectOption | null>(null);
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchTeamOptions = async () => {
      try {
        const response = await getTeamsList(0, 100);
        const options: SelectOption[] = response.teams.map((team) => ({
          key: team.id,
          value: team.name,
        }));
        setTeamOptions(options);

        const nutritionistTeamId = nutritionistDataToEdit?.team_id || '';
        const nutritionistTeamName = nutritionistDataToEdit?.team_name || '';

        if (nutritionistTeamId) {
          const matchedById = options.find((opt) => opt.key === nutritionistTeamId);
          setSelectedTeam(
            matchedById || {
              key: nutritionistTeamId,
              value: nutritionistTeamName || nutritionistTeamId
            }
          );
        } else if (nutritionistTeamName) {
          const matchedByName = options.find(
            (opt) => opt.value === nutritionistTeamName
          );
          setSelectedTeam(
            matchedByName || { key: nutritionistTeamName, value: nutritionistTeamName }
          );
        } else {
          setSelectedTeam(null);
        }
      } catch (error: any) {
        setTeamOptions([]);
        setSelectedTeam(null);
        toast.error(error?.message || 'Failed to load teams');
      }
    };

    fetchTeamOptions();
  }, [isOpen, nutritionistDataToEdit]);

  const clearState = () => {
    dispatch(setNutritionistDataToEdit(null));
    setWasModalChanged(false);
  };

  const onModalClose = (photo: string) => {
    if (wasModalChanged) {
      deleteFile({ file_keys: [photo] });
    }
    setSelectedTeam(null);
    clearState();
    onClose();
  };

  const initialValues: FormValues = nutritionistDataToEdit
    ? {
        first_name: nutritionistDataToEdit.first_name,
        last_name: nutritionistDataToEdit.last_name,
        bio: nutritionistDataToEdit.bio,
        email: nutritionistDataToEdit.email,
        number: nutritionistDataToEdit.phone || '',
        photo: nutritionistDataToEdit.photo!,
        photo_url: nutritionistDataToEdit.photo_url,
        calendly_url: nutritionistDataToEdit.calendly_url || ''
      }
    : {
        first_name: '',
        last_name: '',
        bio: '',
        email: '',
        number: '',
        photo: '',
        photo_url: '',
        calendly_url: ''
      };

  const onUploadPhoto = (base64Photo: string) => {
    setWasModalChanged(true);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, { resetForm }) => {
        if (isFormError) setIsFormError(false);
        setIsLoading(true);
        try {
          const phone =
            values.number![0] !== '+' ? '+' + values.number : values.number;
          const nutritionistData = {
            email: values.email,
            phone: phone.length <= 4 ? undefined : phone,
            bio: values.bio,
            first_name: values.first_name,
            last_name: values.last_name,
            photo: values.photo ?? nutritionistDataToEdit?.photo!,
            team_id: selectedTeam?.key || null,
            calendly_url: values.calendly_url || null
          };

          const res = nutritionistDataToEdit
            ? await updateNutritionist({
                ...nutritionistData,
                user_id: nutritionistDataToEdit.id
              })
            : await createNutritionist(nutritionistData);

          if ('detail' in res && res?.detail) {
            setIsFormError(res?.detail);
            return;
          }

          if (nutritionistDataToEdit) {
            const updatedNutritionistsList = nutritionistsList?.map(
              (nutritionist) => {
                if (nutritionist.id === nutritionistDataToEdit.id) {
                  return {
                    ...nutritionist,
                    ...nutritionistData,
                    team_id: selectedTeam?.key || null,
                    team_name: selectedTeam?.value || '',
                    photo_url: values.photo_url
                  };
                }
                return nutritionist;
              }
            );
            dispatch(setNutritionistsList(updatedNutritionistsList!));
            toast.success('Nutritionist updated successfully');
          } else {
            const response = await getNutritionistsList(0, limit);
            dispatch(setNutritionistsList(response.nutritionists));
            dispatch(
              setNutritionistsCount(
                nutritionistsCount ? nutritionistsCount + 1 : 0
              )
            );
            toast.success('Nutritionist created successfully');
          }

          clearState();
          onClose();
          resetForm();
        } catch (error: any) {
          console.error('Error creating/updating nutritionist:', error);
          setIsFormError(true);
          const errorMessage =
            error?.response?.data?.detail ||
            'Something went wrong. Please try again.';
          toast.error(errorMessage);
        } finally {
          setIsLoading(false);
        }
      }}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({ handleSubmit, values, submitCount, errors, setFieldValue }) => (
        <Modal
          isOpen={isOpen}
          onClose={() => onModalClose(values.photo!)}
          title={
            nutritionistDataToEdit ? 'Edit nutritionist' : 'Add nutritionist'
          }
          width={'520px'}
        >
          <form
            onSubmit={handleSubmit}
            noValidate
            className='nutritionist-modal-container'
          >
            <div className='main-info-container'>
              <div className='section-title'>Main info</div>
              <div className='upload-photo-wrapper'>
                <UploadPhoto
                  onPhotoChange={onUploadPhoto}
                  defaultPhoto={nutritionistDataToEdit ? values.photo_url : ''}
                />
              </div>
              <div className='inputs-wrapper'>
                <TextInput
                  name='name'
                  placeholder='Enter name *'
                  type='text'
                  isError={submitCount > 0 && !!errors.first_name}
                  errorMessage={submitCount > 0 ? errors.first_name : undefined}
                  onChange={(e) => setFieldValue('first_name', e.target.value)}
                  value={values.first_name}
                />
                <TextInput
                  name='name'
                  placeholder='Enter last name *'
                  type='text'
                  isError={submitCount > 0 && !!errors.last_name}
                  errorMessage={submitCount > 0 ? errors.last_name : undefined}
                  onChange={(e) => setFieldValue('last_name', e.target.value)}
                  value={values.last_name}
                />
                <TextInput
                  name='calendly_url'
                  placeholder='Calendly URL'
                  type='text'
                  onChange={(e) =>
                    setFieldValue('calendly_url', e.target.value)
                  }
                  value={values.calendly_url}
                />
                <Select
                  name='team'
                  options={teamOptions}
                  selectedOption={selectedTeam || { key: '', value: '' }}
                  onChange={(option) => setSelectedTeam(option)}
                  isError={false}
                  placeholder='Select team'
                />
                <TextArea
                  name='bio'
                  placeholder='Enter bio *'
                  isError={submitCount > 0 && !!errors.bio}
                  errorMessage={submitCount > 0 ? errors.bio : undefined}
                  onChange={(e) => setFieldValue('bio', e.target.value)}
                  value={values.bio}
                />
              </div>
            </div>
            <div className='contact-info-container'>
              <div className='section-title'>Contact info</div>
              <div className='inputs-wrapper'>
                <TextInput
                  name='email'
                  placeholder='Enter email *'
                  type='email'
                  isError={submitCount > 0 && !!errors.email}
                  errorMessage={submitCount > 0 ? errors.email : undefined}
                  onChange={(e) => setFieldValue('email', e.target.value)}
                  value={values.email}
                />
                <PhoneInputComp
                  placeholder='Enter number'
                  selectedPhone={values.number}
                  onPhoneChange={(phone) => setFieldValue('number', phone)}
                />
              </div>
              <div className='btn-wrapper'>
                <Button
                  text={
                    nutritionistDataToEdit
                      ? 'Edit nutritionist'
                      : 'Add nutritionist'
                  }
                  type='submit'
                  isLoading={isLoading}
                  isDisabled={
                    !!(
                      !values.last_name ||
                      !values.first_name ||
                      !values.email ||
                      !values.bio ||
                      !values.photo
                    )
                  }
                />
                {isFormError && (
                  <div className='error-message'>
                    {typeof isFormError === 'string'
                      ? isFormError
                      : 'Something went wrong. Please try again.'}
                  </div>
                )}
              </div>
            </div>
          </form>
        </Modal>
      )}
    </Formik>
  );
};

export default NutritionistModal;
